import { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { Loading, useAction } from '../components/ui';
import { LEGAL_DOCS, LEGAL_DOCS_PRUEBA, LEGAL_VERSION, LEGAL_VERSION_PRUEBA, legalDoc } from '../legal/textos';
import { useInstanceMode } from '../lib/instance';

/** Enlaces a los textos legales, para el pie de las pantallas públicas. */
export function LegalLinks() {
  const modo = useInstanceMode();
  const docs = modo === 'prueba' ? [...LEGAL_DOCS_PRUEBA, ...LEGAL_DOCS.filter((d) => d.slug === 'aviso-legal')] : LEGAL_DOCS;
  return (
    <p className="hint">
      {docs.map((d, i) => <span key={d.slug}>{i > 0 && ' · '}<Link to={`/legal/${d.slug}`}>{d.title.replace(' de Conforme', '')}</Link></span>)}
    </p>
  );
}

/** Página pública de un texto legal (/legal/:doc). El contenido está en legal/textos.ts. */
export function LegalPage() {
  const { doc } = useParams();
  const d = legalDoc(doc);
  if (!d) return <Navigate to="/legal/condiciones" replace />;
  return (
    <div className="auth-wrap">
      <article className="card stack" style={{ width: 'min(760px, 100%)' }}>
        <strong>Conforme</strong>
        <h1>{d.title}</h1>
        <div className="alert warn">Borrador pendiente de revisión por un abogado. Versión {d.slug.endsWith('-prueba') ? LEGAL_VERSION_PRUEBA : LEGAL_VERSION}.</div>
        {d.sections.map((s) => (
          <section key={s.heading} className="stack">
            <h2>{s.heading}</h2>
            {s.blocks.map((b, i) => Array.isArray(b)
              ? <ul key={i}>{b.map((li) => <li key={li}>{li}</li>)}</ul>
              : <p key={i}>{b}</p>)}
          </section>
        ))}
        <LegalLinks />
        <p className="muted"><Link to="/entrar">Entrar en Conforme</Link></p>
      </article>
    </div>
  );
}

/** Textos legales actualizados: el administrador los acepta antes de seguir (queda registrado). */
export function LegalAccept() {
  const s = useSession();
  const nav = useNavigate();
  const { busy, run } = useAction();
  const [ok, setOk] = useState(false);
  if (s.loading) return <Loading />;
  if (!s.me) return <Navigate to="/entrar" replace />;
  if (!s.me.legal?.mustAccept) return <Navigate to="/" replace />;
  const version = s.me.legal.currentVersion ?? LEGAL_VERSION;
  const sfx = s.me.instance?.mode === 'prueba' ? '-prueba' : '';
  const accept = async () => {
    if (await run(() => api.post('/auth/legal/accept', { version }), 'Condiciones aceptadas')) { await s.refresh(); nav('/'); }
  };
  return (
    <div className="auth-wrap">
      <div className="card auth-card stack" style={{ width: 'min(560px, 100%)' }}>
        <strong>Conforme</strong>
        <h1>Hemos actualizado las condiciones</h1>
        <p className="muted">Para seguir usando Conforme, como administrador de «{s.me.organization?.name}» tienes que aceptar la versión {version} de estos textos{s.me.legal.acceptedVersion ? ` (la anterior aceptada es la ${s.me.legal.acceptedVersion})` : ''}:</p>
        <ul>
          <li><Link to={`/legal/condiciones${sfx}`} target="_blank">Condiciones de uso</Link></li>
          <li><Link to={`/legal/encargado${sfx}`} target="_blank">Contrato de encargado del tratamiento</Link> (RGPD)</li>
          <li><Link to={`/legal/privacidad${sfx}`} target="_blank">Política de privacidad</Link></li>
        </ul>
        <p className="hint">El cambio principal: Conforme funciona como pasarela. Procesa tus facturas y te las devuelve; conserva los originales 100 días (con aviso 10 días antes) y la obligación de guardarlas 6 años es de tu empresa.</p>
        <label className="check"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} /> <span>He leído y acepto las condiciones de uso y el contrato de encargado del tratamiento, y he leído la política de privacidad.</span></label>
        <button className="primary" disabled={busy || !ok} onClick={accept}>Acepto</button>
        <button type="button" className="ghost" onClick={async () => { await s.logout(); nav('/entrar'); }}>Salir</button>
      </div>
    </div>
  );
}

/** Aviso para quien no es administrador mientras su organización no acepte los textos nuevos. */
export function LegalPendingNotice() {
  const s = useSession();
  if (!s.me?.legal?.pending || s.me.legal.mustAccept) return null;
  return <div className="alert warn" style={{ marginBottom: 12 }}>Hemos actualizado las condiciones de uso de Conforme. Un administrador de tu organización debe aceptarlas al entrar.</div>;
}
